import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { detectLocale, persistLocale, applyDocumentDirection, clearLocale } from '../utils/localeDetection';
import { DEFAULT_LOCALE } from '../constants';
import { isLanguageSelectorEnabled } from '../components/Utils';

interface LocaleContextValue {
    locale: string;
    setLocale: (locale: string) => void;
    resetLocale: () => void;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

interface LocaleProviderProps {
    children: React.ReactNode;
}

/**
 * Get initial locale based on whether language selector is enabled for this site.
 * - Enabled sites: Use browser locale detection
 * - Non-enabled sites: Force English (DEFAULT_LOCALE) for consistent experience
 */
function getInitialLocale(): string {
    const urlParams = new URLSearchParams(window.location.search);
    const siteCode = urlParams.get('site_code');

    if (isLanguageSelectorEnabled(siteCode)) {
        return detectLocale();
    }

    return DEFAULT_LOCALE;
}

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children }) => {
    const [locale, setLocaleState] = useState<string>(() => getInitialLocale());

    const setLocale = useCallback((newLocale: string) => {
        persistLocale(newLocale);
        applyDocumentDirection(newLocale);
        setLocaleState(newLocale);
    }, []);

    const resetLocale = useCallback(() => {
        clearLocale();
        const newLocale = getInitialLocale();
        applyDocumentDirection(newLocale);
        setLocaleState(newLocale);
    }, []);

    // Apply document direction on initial mount based on detected locale
    useEffect(() => {
        applyDocumentDirection(locale);
    }, [locale]);

    const value = useMemo(
        () => ({ locale, setLocale, resetLocale }),
        [locale, setLocale, resetLocale]
    );

    return (
        <LocaleContext.Provider value={value}>
            {children}
        </LocaleContext.Provider>
    );
};

export const useLocale = (): LocaleContextValue => {
    const context = useContext(LocaleContext);
    if (!context) {
        throw new Error('useLocale must be used within a LocaleProvider');
    }
    return context;
};
